import {ReactElement, useContext, useEffect} from 'react';
import {ListboxItemProps} from '@common/ui/forms/listbox/item';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useLogout} from '@common/auth/requests/logout';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {useSettings} from '@common/core/settings/use-settings';
import {useAuth} from '@common/auth/use-auth';
import {useNavigate} from '@common/utils/hooks/use-navigate';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {
  Menu,
  MenuItem,
  MenuTrigger,
} from '@common/ui/navigation/menu/menu-trigger';
import {NotificationsIcon} from '@common/icons/material/Notifications';
import {Trans} from '@common/i18n/trans';
import {PaymentsIcon} from '@common/icons/material/Payments';
import {createSvgIconFromTree} from '@common/icons/create-svg-icon';
import {AccountCircleIcon} from '@common/icons/material/AccountCircle';
import {DarkModeIcon} from '@common/icons/material/DarkMode';
import {LightModeIcon} from '@common/icons/material/LightMode';
import {ExitToAppIcon} from '@common/icons/material/ExitToApp';
import {InstallMobileIcon} from '@common/icons/material/InstallMobile';
import {IosShareIcon} from '@common/icons/material/IosShare';
import {usePwaInstall} from '@common/hooks/use-pwa-install';
import {openDialog} from '@common/ui/overlays/store/dialog-store';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {DialogFooter} from '@common/ui/overlays/dialog/dialog-footer';
import {Button} from '@common/ui/buttons/button';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import {toast} from '@common/ui/toast/toast';

// iOS Install Instructions Dialog
function IosInstallDialog() {
  const {close} = useDialogContext();

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="Add to Home Screen"/>
      </DialogHeader>
      <DialogBody>
        <div style={{padding: '10px 0'}}>
          <p style={{marginBottom: '20px', fontSize: '15px'}}>
            <strong>To install this web app on your iOS device:</strong>
          </p>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', fontSize: '15px'}}>
            <span style={{fontWeight: 'bold', minWidth: '20px'}}>1.</span>
            <span>Tap the <strong>Share</strong> button</span>
            <IosShareIcon style={{color: '#007AFF', fontSize: '26px', flexShrink: 0}}/>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px', fontSize: '15px'}}>
            <span style={{fontWeight: 'bold', minWidth: '20px'}}>2.</span>
            <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()} variant="flat" color="primary">
          <Trans message="Got it"/>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function ChromeInstallDialog() {
  const {close} = useDialogContext();

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="Install on Chrome"/>
      </DialogHeader>
      <DialogBody>
        <div style={{padding: '10px 0'}}>
          <p style={{marginBottom: '16px', fontSize: '15px'}}>
            <strong>To install this app using Chrome:</strong>
          </p>
          <div style={{display: 'flex', alignItems: 'center'}}>
            <p style={{fontSize: '14px'}}>
              Click this icon in your URL bar:
            </p>
            <img
              src="/icon/install_desktop_google.png"
              alt="Chrome install button"
              style={{
                width: '32px',
                height: '32px',
                marginLeft: '8px',
              }}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()} variant="flat" color="primary">
          <Trans message="Got it"/>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function EdgeInstallDialog() {
  const {close} = useDialogContext();

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="Install on Microsoft Edge"/>
      </DialogHeader>
      <DialogBody>
        <div style={{padding: '10px 0'}}>
          <p style={{marginBottom: '16px', fontSize: '15px'}}>
            <strong>To install this app using Microsoft Edge:</strong>
          </p>
          <ol style={{marginLeft: '18px', marginBottom: '0', fontSize: '15px'}}>
            <li style={{marginBottom: '10px'}}>
              Click the three dots menu (<strong>⋮</strong>) in the top-right corner of Edge.
            </li>
            <li style={{marginBottom: '10px'}}>
              Go to <strong>Apps → Install this site as an app</strong>.
            </li>
            <li>
              Confirm the installation when prompted.
            </li>
          </ol>
          <p style={{marginTop: '14px', fontSize: '14px'}}>
            Tip: You might also see an install icon in the address bar.
          </p>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()} variant="flat" color="primary">
          <Trans message="Got it"/>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// Desktop Safari Install Instructions Dialog
function SafariDesktopInstallDialog() {
  const {close} = useDialogContext();

  return (
    <Dialog size="sm">
      <DialogHeader>
        <Trans message="Add to Dock (Safari)"/>
      </DialogHeader>
      <DialogBody>
        <div style={{padding: '10px 0'}}>
          <p style={{marginBottom: '16px', fontSize: '15px'}}>
            <strong>To add this app using Safari on macOS:</strong>
          </p>
          <ol style={{marginLeft: '18px', marginBottom: '0', fontSize: '15px'}}>
            <li style={{marginBottom: '10px'}}>
              Click the Share button in the toolbar ({' '}
              <IosShareIcon style={{color: '#007AFF', fontSize: '20px', verticalAlign: 'middle'}}/> ).
            </li>
            <li style={{marginBottom: '10px'}}>
              Select <strong>"Add to Dock"</strong>.
            </li>
            <li>
              Adjust the name if you like, then click <strong>Add</strong>.
            </li>
          </ol>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={() => close()} variant="flat" color="primary">
          <Trans message="Got it"/>
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface Props {
  children: ReactElement;
  items?: ReactElement<ListboxItemProps>[];
}

export function NavbarAuthMenu({children, items}: Props) {
  const {auth} = useContext(SiteConfigContext);
  const logout = useLogout();
  const menu = useCustomMenu('auth-dropdown');
  const {notifications, themes} = useSettings();
  const {user, isSubscribed} = useAuth();
  const navigate = useNavigate();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const {isInstallable, isInstalled, showIosInstructions, install} = usePwaInstall();
  const hasUnreadNotif = !!user?.unread_notifications_count;

  // Check if browser is Chrome (desktop or mobile)
  const isChrome = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /chrome/.test(userAgent) && !/edg/.test(userAgent) && !/opr/.test(userAgent);
  };

  const isChromeDesktop = () =>
    isChrome() && !/android/.test(window.navigator.userAgent.toLowerCase());

  const isEdgeDesktop = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /edg/.test(userAgent);
  };

  const isSafariDesktop = () => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isMac = /macintosh|mac os x/.test(userAgent);
    return isMac && /safari/.test(userAgent) && !/chrome/.test(userAgent);
  };

  const chromeDesktop = isChromeDesktop();
  const edgeDesktop = isEdgeDesktop();
  const safariDesktop = isSafariDesktop();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isInstalled) return;
    if (!chromeDesktop) return;
    const storageKey = 'pwa-install-reminder-shown';
    const hasShown = sessionStorage.getItem(storageKey);
    if (hasShown) return;
    const timer = window.setTimeout(() => {
      toast(
        'Tip: Look for the desktop-with-download icon (🖥️⬇️) in Chrome’s address bar or use the “Install PrivatLocker” option in the menu to add the app to your desktop.',
      );
      sessionStorage.setItem(storageKey, 'true');
    }, 10000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [isInstalled, chromeDesktop]);

  if (!selectedTheme || !user) return null;

  const notifMenuItem = (
    <MenuItem
      className="md:hidden"
      value="notifications"
      startIcon={<NotificationsIcon/>}
      onSelected={() => {
        navigate('/notifications');
      }}
    >
      <Trans message="Notifications"/>
      {hasUnreadNotif ? ` (${user.unread_notifications_count})` : undefined}
    </MenuItem>
  );

  const billingMenuItem = (
    <MenuItem
      value="billing"
      startIcon={<PaymentsIcon/>}
      onSelected={() => {
        navigate('/billing');
      }}
    >
      <Trans message="Billing"/>
    </MenuItem>
  );

  return (
    <MenuTrigger>
      {children}
      <Menu>
        {menu &&
          menu.items.map(item => {
            const Icon = item.icon && createSvgIconFromTree(item.icon);
            return (
              <MenuItem
                value={item.id}
                key={item.id}
                startIcon={Icon && <Icon/>}
                onSelected={() => {
                  if (item.type === 'link') {
                    window.open(item.action, '_blank');
                  } else {
                    navigate(item.action);
                  }
                }}
              >
                <Trans message={item.label}/>
              </MenuItem>
            );
          })}
        {auth.getUserProfileLink && (
          <MenuItem
            value="profile"
            startIcon={<AccountCircleIcon/>}
            onSelected={() => {
              navigate(auth.getUserProfileLink!(user));
            }}
          >
            <Trans message="Profile page"/>
          </MenuItem>
        )}
        {items?.map(item => item)}
        {notifications?.integrated ? notifMenuItem : undefined}
        {isSubscribed && billingMenuItem}
        {themes?.user_change && !selectedTheme.is_dark && (
          <MenuItem
            value="light"
            startIcon={<DarkModeIcon/>}
            onSelected={() => {
              selectTheme('dark');
            }}
          >
            <Trans message="Dark mode"/>
          </MenuItem>
        )}
        {themes?.user_change && selectedTheme.is_dark && (
          <MenuItem
            value="dark"
            startIcon={<LightModeIcon/>}
            onSelected={() => {
              selectTheme('light');
            }}
          >
            <Trans message="Light mode"/>
          </MenuItem>
        )}
        <MenuItem
          value="add-to-home"
          startIcon={<InstallMobileIcon/>}
          onSelected={async () => {
            if (isInstalled) {
              // App is already installed - do nothing or show message
              return;
            }
            if (isInstallable) {
              // For Chrome/Edge that exposed beforeinstallprompt - trigger the install prompt
              const promptShown = await install();
              if (!promptShown) {
                if (chromeDesktop) {
                  openDialog(ChromeInstallDialog);
                } else if (edgeDesktop) {
                  openDialog(EdgeInstallDialog);
                }
              }
            } else if (showIosInstructions) {
              // For iOS Safari - show custom dialog with icon
              openDialog(IosInstallDialog);
            } else if (chromeDesktop) {
              openDialog(ChromeInstallDialog);
            } else if (edgeDesktop) {
              openDialog(EdgeInstallDialog);
            } else if (safariDesktop) {
              openDialog(SafariDesktopInstallDialog);
            } else {
              // For unsupported browsers - show info message
              alert('To add this app to your home screen, please use Safari on iOS or Chrome/Edge on desktop.');
            }
          }}
        >
          {isInstalled ? (
            <Trans message="App is Installed"/>
          ) : (
            <Trans message="Add to Home"/>
          )}
        </MenuItem>
        <MenuItem
          value="logout"
          startIcon={<ExitToAppIcon/>}
          onSelected={() => {
            logout.mutate();
          }}
        >
          <Trans message="Log out"/>
        </MenuItem>
      </Menu>
    </MenuTrigger>
  );
}
